
import HTTP from "./http";

const CACHE_TTL_MS = 5000;
const cache = new Map();
const inflight = new Map();

const getCacheKey = (endpoint, params) => {
    return `${endpoint}:${JSON.stringify(params || {})}`;
};

const cachedCall = async (key, fetchFn) => {
    const now = Date.now();
    const cached = cache.get(key);
    if (cached && (now - cached.timestamp) < CACHE_TTL_MS) {
        return cached.data;
    }
    // Dedupe concurrent callers: if a request for this key is already in flight,
    // await the same promise instead of issuing N parallel HTTP calls.
    const pending = inflight.get(key);
    if (pending) {
        return pending;
    }
    const promise = (async () => {
        try {
            const data = await fetchFn();
            cache.set(key, { data, timestamp: Date.now() });
            return data;
        } finally {
            inflight.delete(key);
        }
    })();
    inflight.set(key, promise);
    return promise;
};

const API = (baseUrl = '') => {

    // baseUrl = 'https://cts2.giftstarr.cards/api';
    baseUrl = baseUrl || (window.GIFTSTARR_CFG && window.GIFTSTARR_CFG.API_URL) || '/api'
    const http = HTTP(baseUrl);

    const CRUNCH_ACTION_CODES = {
        OK: "000"
    }

    const getGiftInfo= async (code) => {
        if(!code) return {error:"missing gift code"}
        const key = getCacheKey('/getGiftInfo', code);
        return cachedCall(key, () => http.get('/getGiftInfo/'+code));
    }

    const scheduleEmailToBeneficiary = async (code, giftInfoUpdate={}) => {
        if(!code) return {error:"missing gift code"}
        return http.post('/sendVideoMessage/' + code, giftInfoUpdate)
    }

    const getClientConfig = async () => {
        const key = getCacheKey('/clientConfig');
        return cachedCall(key, () => http.get('/clientConfig'));
    }

    const cardCheck = async (token) => {
        const key = getCacheKey('/card', token);
        return cachedCall(key, () => http.get('/card/' + token));
    }

    const balanceCheck = async (token) => {
        const key = getCacheKey('/balance', token);
        return cachedCall(key, () => http.get('/balance/' + token));
    }

    const cardStatement = async (token) => {
        const key = getCacheKey('/statement', token);
        return cachedCall(key, () => http.get('/statement/' + token));
    }

    const resetVideoPassword = async (token, password) => {
        return http.post("/video-password/" + token, {'password': password});
    }

    const hasVideoCheck = async (token) => {
        const key = getCacheKey('/has-video', token);
        const response = await cachedCall(key, () => http.get("/has-video/" + token));
        return response === "YES"
    }

    const getVideoUrl = async (token, password) => {
        const key = getCacheKey('/video', { token, password });
        return cachedCall(key, () => http.get("/video/" + token, {'password': password}));
    }

    const deleteVideo = async (token, password, recaptchaToken = null) => {
        const result = await http.del("/video/" + token, {password: password}, recaptchaToken);
        // Invalidate video-related cache entries for this token
        cache.delete(getCacheKey('/has-video', token));
        cache.delete(getCacheKey('/video', { token, password }));
        return result;
    }

    const registerCardUser = async (user, token, recaptchaToken = null) => {
        return http.post("/giftcardusers/register/" + token, user, recaptchaToken);
    }

    const activateCard = async(token, activationCode, recaptchaToken = null) => {
        const key = getCacheKey('/activate', { token, activationCode });
        // Note: GET requests don't support recaptchaToken in this implementation
        // If needed, convert to POST or add header support to get()
        return cachedCall(key, () => http.get("/activate/" + token + "?code=" + activationCode));
    }


    // PCI Widget API methods
    // Start reset PIN process to send the OTP code over email
    const startResetPin = async (token, recaptchaToken = null) => {
        return http.post(`/${token}/reset-pin`, {}, recaptchaToken);
    }

    // Get the PIN reset widget URL using OTP code
    const getPinResetWidgetUrl = async (token, code) => {
        const headers = {};
        return http.put(`/${token}/reset-pin?code=${code}`, {}, headers);
    }

    // --- giftstarr_api_v2 endpoints (proxied via /api/v2/*) ---

    const createFingerprint = async (webData) => {
        return http.post('/v2/fingerprint', { device_type: 'web', web_data: webData });
    }

    const validateCardV2 = async (token, last4, fingerprintId) => {
        return http.post(`/v2/token/${token}`, { last4 }, null, { 'X-Fingerprint-ID': fingerprintId });
    }

    const getGoogleWalletOpc = async (cardId, walletData, fingerprintId) => {
        return http.post(`/v2/card/${cardId}/googlewallet`, walletData, null, { 'X-Fingerprint-ID': fingerprintId });
    }

    const getAppleWalletJws = async (cardId, fingerprintId) => {
        return http.get(`/v2/card/${cardId}/applewallet`, { 'X-Fingerprint-ID': fingerprintId });
    }

    // --- Shared wallet provisioning subdomain handoff ---

    const createWalletProvisioningSession = async (payload) => {
        return http.post('/wallet/provisioning-session', payload);
    }

    const getWalletProvisioningSession = async (ticket) => {
        return http.get(`/wallet/provisioning-session/${ticket}`);
    }

    const reportWalletProvisioningResult = async (ticket, result, error = null) => {
        return http.post(`/wallet/provisioning-session/${ticket}/result`, { result, error });
    }

    const reportWalletProvisioningLog = async (ticket, event, payload = null, timestamp = null) => {
        return http.post(`/wallet/provisioning-session/${ticket}/log`, {
            event,
            payload,
            timestamp: timestamp || new Date().toISOString(),
        });
    }

    const uploadFiles = async (file, token, password, onProgress, onStart=null, recaptchaToken=null, timeoutMs=60000) => {
      const url = baseUrl + "/video/" + token;
      const promise = new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.upload.addEventListener('progress', e => onProgress(e.loaded / e.total));
          xhr.addEventListener('load', () => resolve({status: xhr.status, body: xhr.responseText}));
          xhr.addEventListener('error', () => reject(new Error('File upload failed')));
          xhr.addEventListener('abort', () => reject(new Error('File upload aborted')));
          xhr.addEventListener('timeout', () => reject(new Error('File upload timed out')));
          xhr.timeout = timeoutMs;
          xhr.open('POST', url, true);
          if (recaptchaToken) {
              xhr.setRequestHeader('X-Recaptcha-Token', recaptchaToken);
          }
          const formData = new FormData();
          formData.append("password", password);
          formData.append("file", file);
          console.log(formData);
          if(onStart) onStart(xhr);
          xhr.send(formData);
      });
      return promise;
  }

    const getVideoStatus = async (token) => {
        return http.get("/video-status/" + token);
    }

    const addressLookup = async (postcode) => {
        return http.get('/cnc/address-lookup?postcode=' + encodeURIComponent(postcode));
    }

    return {
        getGiftInfo  : getGiftInfo,
        scheduleEmailToBeneficiary: scheduleEmailToBeneficiary,
        getClientConfig: getClientConfig,
        addressLookup: addressLookup,
        balanceCheck : balanceCheck,
        cardCheck : cardCheck,
        cardStatement : cardStatement,
        resetVideoPassword: resetVideoPassword,
        uploadFiles : uploadFiles,
        getVideoStatus: getVideoStatus,
        hasVideoCheck: hasVideoCheck,
        getVideoUrl : getVideoUrl,
        deleteVideo : deleteVideo,
        registerCardUser: registerCardUser,
        activateCard: activateCard,
        startResetPin: startResetPin,
        getPinResetWidgetUrl: getPinResetWidgetUrl,
        createFingerprint: createFingerprint,
        validateCardV2: validateCardV2,
        getGoogleWalletOpc: getGoogleWalletOpc,
        getAppleWalletJws: getAppleWalletJws,
        createWalletProvisioningSession: createWalletProvisioningSession,
        getWalletProvisioningSession: getWalletProvisioningSession,
        reportWalletProvisioningResult: reportWalletProvisioningResult,
        reportWalletProvisioningLog: reportWalletProvisioningLog,

        ACTION_CODE: CRUNCH_ACTION_CODES,


    }
}

export default API